import {
  getDoc,
  doc,
  collection,
  query,
  where,
  getDocs,
  DocumentReference,
  documentId,
  DocumentData,
  CollectionReference,
  onSnapshot,
} from 'firebase/firestore';
import { firestore } from '~/firebase/init';
import { EnterpriseGroupBuyStore } from '~/types/enterpriseGroupBuy';
import * as fp from 'lodash/fp';
import { ItemWithId } from '~/types/store/item';
import { StoreWithId } from '~/types/store/store';
import getDateInventory from '~/utils/getDateInventory';
import isInOperatingTime from '~/utils/isInOperatingTime';
import isNotInHoliday from '~/utils/isNotInHoliday';
import isCanPreOrderTime from '~/utils/isCanPreOrderTime';
import getMultiStoreMenus from './getMultiStoreMenus';

const fetchItemsInBatches = async (
  itemsCollection: CollectionReference<DocumentData>,
  itemIds: Array<string>,
): Promise<Array<ItemWithId>> => {
  const BATCH_SIZE = 10;
  const batches = [];

  for (let i = 0; i < itemIds.length; i += BATCH_SIZE) {
    const batch = itemIds.slice(i, i + BATCH_SIZE);
    batches.push(batch);
  }

  const promises = batches.map((batch) =>
    getDocs(query(itemsCollection, where(documentId(), 'in', batch))),
  );

  const results = await Promise.all(promises);

  const allDocs: Array<ItemWithId> = [];
  results.forEach((querySnapshot) => {
    querySnapshot.forEach((doc) => allDocs.push({ ...doc.data(), id: doc.id } as ItemWithId));
  });

  return allDocs;
};

export type Result = { storeData: StoreWithId; allItems: boolean; items: Array<ItemWithId> };

const handleGetStoreInfoAndItems = async (
  groupBuyStoreInfos: EnterpriseGroupBuyStore,
  pickMealAt: Date,
): Promise<Partial<Result>> => {
  const storeDocRef = doc(
    firestore,
    'stores',
    groupBuyStoreInfos.id,
  ) as DocumentReference<StoreWithId>;

  const storeSnapshot = await getDoc(storeDocRef);

  if (!storeSnapshot.exists()) return { storeData: undefined, allItems: false, items: [] };
  const storeData = {
    ...storeSnapshot.data(),
    id: storeSnapshot.id,
  } as StoreWithId;

  if (groupBuyStoreInfos.itemIds?.length > 0 || Boolean(groupBuyStoreInfos?.isAll)) {
    const itemsCollection = collection(firestore, 'stores', groupBuyStoreInfos.id, 'items');

    if (groupBuyStoreInfos.isAll) {
      const isCanOrder =
        storeData &&
        pickMealAt &&
        isCanPreOrderTime(
          storeData?.preOrderSetting?.schedule,
          storeData?.preOrderSetting?.afterHours,
          // 企業團購不受到最早下單時間限制
          Infinity,
          pickMealAt,
          new Date(),
        ) &&
        isInOperatingTime(storeData?.preOrderSetting?.schedule, pickMealAt) &&
        isNotInHoliday(
          (storeData?.officialHoliday || [])?.map((i) => i?.toDate()),
          pickMealAt,
        );

      if (!isCanOrder) return { storeData, allItems: false, items: [] };

      const menus = await getMultiStoreMenus([storeData.id]);
      const filterMenus = menus
        .filter(({ allowPublish }) => allowPublish)
        .filter(({ schedule }) => isInOperatingTime(schedule, pickMealAt));

      const canOrderItemIds = filterMenus.flatMap((menu) => menu.items || []) || [];

      const itemSnapshots = await getDocs(itemsCollection);
      const items = (itemSnapshots?.docs || [])
        ?.map((i) => ({ ...i.data(), id: i.id } as ItemWithId))
        .filter(
          (i) =>
            i.isAvailable &&
            fp.gt(getDateInventory(i.inventory, pickMealAt), 0) &&
            canOrderItemIds.includes(i?.id),
        );

      return {
        storeData,
        allItems: true,
        items: items.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        }),
      };
    }

    if (!groupBuyStoreInfos.isAll) {
      const items = await fetchItemsInBatches(itemsCollection, groupBuyStoreInfos.itemIds);
      return {
        storeData,
        allItems: false,
        items: items.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        }),
      };
    }
  }
  return { storeData, allItems: false, items: [] };
};

const createStoreAndItemsListener = (
  groupBuyStoreInfos: EnterpriseGroupBuyStore,
  callback: (result: Partial<Result>) => void,
) => {
  const storeDocRef = doc(
    firestore,
    'stores',
    groupBuyStoreInfos.id,
  ) as DocumentReference<StoreWithId>;

  // 监听商店文档
  const unsubscribeStore = onSnapshot(storeDocRef, (storeSnapshot) => {
    if (!storeSnapshot.exists()) {
      callback({ storeData: undefined, allItems: false, items: [] });
      return;
    }

    const storeData = {
      ...storeSnapshot.data(),
      id: storeSnapshot.id,
    } as StoreWithId;

    if (groupBuyStoreInfos.itemIds?.length > 0 || Boolean(groupBuyStoreInfos?.isAll)) {
      const itemsCollection = collection(firestore, 'stores', groupBuyStoreInfos.id, 'items');

      const unsubscribeItems = onSnapshot(itemsCollection, (itemSnapshots) => {
        if (groupBuyStoreInfos.isAll) {
          const items = itemSnapshots.docs.map((i) => ({ ...i.data(), id: i.id } as ItemWithId));

          callback({
            storeData,
            allItems: true,
            items,
          });
        } else {
          const items = itemSnapshots.docs
            .filter((doc) => groupBuyStoreInfos.itemIds.includes(doc.id))
            .map((i) => ({ ...i.data(), id: i.id } as ItemWithId));

          callback({
            storeData,
            allItems: false,
            items,
          });
        }
      });

      return () => {
        unsubscribeStore();
        unsubscribeItems();
      };
    } else {
      callback({ storeData, allItems: false, items: [] });
    }
  });

  return unsubscribeStore;
};
// const unsubscribe = createStoreAndItemsListener(groupBuyStoreInfos, (result) => {
//   console.log('Updated result:', result);
//   // 在这里处理更新后的数据
// });

// // 当不再需要监听时，调用返回的函数来取消订阅
// // unsubscribe();

export default async function getEnterpriseGroupBuyStoreAndItems(
  storesMap: Map<string, EnterpriseGroupBuyStore>,
  pickMealAt: Date,
): Promise<Array<Result>> {
  const resultsAsync = [...storesMap.values()]
    .sort((a, b) => a.index - b.index)
    .map((store) => handleGetStoreInfoAndItems(store, pickMealAt));
  const results = await Promise.all(resultsAsync);

  return results.filter((d): d is Result => !!d.storeData && !!((d?.items || [])?.length > 0));
}
